import { Box, Button, Flex, Text } from "@chakra-ui/react";
import FileHandler from "@tiptap/extension-file-handler";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { TextStyleKit } from "@tiptap/extension-text-style";
import type { Content } from "@tiptap/react";
import { Editor, EditorContent, ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { ProjectDocumentDto, UploadDto, ViewDocumentAsOwnerDto, ViewSharedDocumentDto } from "../../../autogen/bff-api";
import { useDocumentDownloader } from "../../documents/DownloadDocumentIconButton/useDocumentDownloader";
import { getLastSavedLabel } from "../../formatting/getLastSavedLabel";
import { MenuBar, type ToolbarConfig } from "./MenuBar";
import { MAX_IMAGE_WIDTH, ResizableImage } from "./ResizableImage";
import "./TipTap.scss";

export type TipTapDocument = ViewDocumentAsOwnerDto | ProjectDocumentDto | ViewSharedDocumentDto;

export type TipTapCreatePostRequest = (params: { uploadId: string; file: File }) => Promise<UploadDto>;

export type TipTapCopyTemplateDocumentsResult = {
  uploadIdMapping: Record<string, string>; // old fromUploadId → new uploadId
  srcMapping: Record<string, string>; // old fromUploadId → blob URL for preview
};

export type TipTapCopyTemplateDocumentsHandler = (params: {
  templateDocuments: ViewDocumentAsOwnerDto[];
}) => Promise<TipTapCopyTemplateDocumentsResult>;

export type TipTapDocumentRemoveHandler = (params: { documentId: string }) => void | Promise<void>;

type HandleFileUpload = (params: { editor: Editor; files: File[]; position?: number }) => void;

const getUploadIdsFromEditor = (editor: Editor): Set<string> => {
  const uploadIds = new Set<string>();
  editor.state.doc.descendants((node) => {
    if (node.type.name === "image" && typeof node.attrs.uploadId === "string") {
      uploadIds.add(node.attrs.uploadId);
    }
  });
  return uploadIds;
};

const ImageWithUploadId = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      uploadId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-upload-id"),
        renderHTML: (attributes) => (attributes.uploadId ? { "data-upload-id": attributes.uploadId as string } : {}),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width") || element.style.width?.replace("px", "") || null,
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width as string } : {}),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height") || element.style.height?.replace("px", "") || null,
        renderHTML: (attributes) => (attributes.height ? { height: attributes.height as string } : {}),
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImage);
  },
});

const getImageDimensions = (src: string): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    const img = document.createElement("img");
    img.onload = () => {
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      // Cap dimensions to max width while preserving aspect ratio
      if (naturalWidth > MAX_IMAGE_WIDTH) {
        const scale = MAX_IMAGE_WIDTH / naturalWidth;
        resolve({
          width: Math.round(naturalWidth * scale),
          height: Math.round(naturalHeight * scale),
        });
      } else {
        resolve({ width: naturalWidth, height: naturalHeight });
      }
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = src;
  });
};

export const uploadImageToS3 = async ({ upload, file }: { upload: UploadDto; file: File }) => {
  const formData = new FormData();

  formData.append("x-amz-date", upload.renamedXAmzDate);
  formData.append("x-amz-signature", upload.renamedXAmzSignature);
  formData.append("x-amz-algorithm", upload.renamedXAmzAlgorithm);
  formData.append("x-amz-credential", upload.renamedXAmzCredential);
  formData.append("policy", upload.policy);
  formData.append("key", upload.key);
  formData.append("file", file);

  const response = await fetch(upload.url, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Image upload failed");
  }
};

const insertImageNode = ({
  editor,
  src,
  uploadId,
  position,
  width,
  height,
}: {
  editor: Editor;
  src: string | ArrayBuffer | null;
  uploadId?: string;
  position?: number;
  width?: number;
  height?: number;
}) => {
  editor
    .chain()
    .insertContentAt(position ?? editor.state.selection.anchor, {
      type: "image",
      attrs: {
        src,
        uploadId,
        width,
        height,
      },
    })
    .focus()
    .run();
};

const removeImageNode = ({ editor, uploadId }: { editor: Editor; uploadId: string }) => {
  const transaction = editor.state.tr;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.uploadId === uploadId) {
      transaction.delete(pos, pos + node.nodeSize);
    }
  });

  if (transaction.docChanged) {
    editor.view.dispatch(transaction);
  }
};

const updateImageNodeByUploadId = ({ editor, uploadId, src }: { editor: Editor; uploadId: string; src: string }) => {
  const transaction = editor.state.tr;
  let foundUpdates = false;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.uploadId === uploadId && node.attrs.src !== src) {
      transaction.setNodeMarkup(pos, undefined, { ...node.attrs, src });
      foundUpdates = true;
    }
  });

  if (foundUpdates && transaction.docChanged) {
    editor.view.dispatch(transaction);
  }

  return foundUpdates;
};

const insertImageFromReader = ({ editor, file, position }: { editor: Editor; file: File; position?: number }) => {
  const fileReader = new FileReader();

  fileReader.readAsDataURL(file);
  fileReader.onload = () => {
    if (typeof fileReader.result === "string" || fileReader.result instanceof String) {
      insertImageNode({ editor, src: fileReader.result, position });
    }
  };
};

const createTipTapExtensions = (handleFileUpload: HandleFileUpload) => [
  StarterKit.configure({
    link: {
      openOnClick: true,
      autolink: true,
      linkOnPaste: true,
      defaultProtocol: "https",
    },
  }),
  Highlight.configure({ multicolor: true }),
  TableKit.configure({
    table: {
      resizable: true,
    },
  }),
  TextStyleKit.configure({}),
  ImageWithUploadId,
  FileHandler.configure({
    allowedMimeTypes: ["image/png", "image/jpeg", "image/gif", "image/webp"],
    onDrop: (currentEditor, files, pos) =>
      handleFileUpload({
        editor: currentEditor,
        files,
        position: pos,
      }),
    onPaste: (currentEditor, files, htmlContent) => {
      if (htmlContent) {
        return false;
      }

      handleFileUpload({
        editor: currentEditor,
        files,
        position: currentEditor.state.selection.anchor,
      });

      return true;
    },
  }),
];

const defaultHandleFileUpload: HandleFileUpload = ({ editor, files, position }) => {
  files.forEach((file) => {
    insertImageFromReader({ editor, file, position });
  });
};

export const extensions = createTipTapExtensions(defaultHandleFileUpload);

export const parseTiptapContent = (value?: string) => {
  try {
    const parsed = JSON.parse(value ?? "");
    return parsed;
  } catch (e) {
    return value;
  }
};


export const TipTap = ({
  content,
  editable = true,
  onUpdate,
  onDocumentRemove,
  hasBorder = true,
  padding = "2",
  maxHeight = "96",
  createPostRequest,
  documents,
  debounceWait = 15000,
  debounceMaxWait = 30000,
  toolbar,
}: {
  content: string;
  editable?: boolean;
  onUpdate?: (value: string) => void | Promise<void>;
  onDocumentRemove?: TipTapDocumentRemoveHandler;
  hasBorder?: boolean;
  padding?: string;
  maxHeight?: string;
  createPostRequest?: TipTapCreatePostRequest;
  documents?: TipTapDocument[];
  debounceWait?: number;
  debounceMaxWait?: number;
  toolbar?: ToolbarConfig;
}) => {
  const { t } = useTranslation();
  const { getDownloadLink } = useDocumentDownloader();

  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastSavedLabel, setLastSavedLabel] = useState<string | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const previousUploadIdsRef = useRef<Set<string>>(new Set());
  const onUpdateRef = useRef(onUpdate);

  // Keep the ref up to date without recreating the debounced function
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const debouncedOnUpdate = useMemo(() => {
    const updateFn = async (value: string) => {
      if (!onUpdateRef.current) return;
      await onUpdateRef.current(value);
      setLastSavedAt(new Date());
      setHasPendingChanges(false);
    };

    // When debounceWait is 0, call instantly without debouncing
    if (debounceWait === 0) {
      const noop = () => undefined;
      return Object.assign(updateFn, { flush: noop, cancel: noop });
    }

    return debounce(updateFn, debounceWait, { maxWait: debounceMaxWait });
  }, [debounceWait, debounceMaxWait]);

  // Update label periodically
  useEffect(() => {
    if (!lastSavedAt) {
      setLastSavedLabel(null);
      return;
    }

    const updateLabel = () => {
      setLastSavedLabel(getLastSavedLabel({ lastSavedAt, t }));
    };

    updateLabel();
    const interval = setInterval(updateLabel, 30000);

    return () => clearInterval(interval);
  }, [lastSavedAt, t]);

  // Flush on beforeunload (tab close/refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      debouncedOnUpdate.flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [debouncedOnUpdate]);

  // Flush on unmount (navigation within app)
  useEffect(() => {
    return () => {
      debouncedOnUpdate.flush();
    };
  }, [debouncedOnUpdate]);
  const [resolvedDocUrls, setResolvedDocUrls] = useState<Record<string, string>>({});
  const [failedDocIds, setFailedDocIds] = useState<Set<string>>(new Set());
  const objectUrlCache = useRef<Record<string, string>>({});
  const inFlightDocIds = useRef<Set<string>>(new Set());
  const suppressOnUpdate = useRef<boolean>(false);

  const handleFileUpload = useCallback<HandleFileUpload>(
    ({ editor, files, position }) => {
      if (!createPostRequest) {
        files.forEach((file) => {
          insertImageFromReader({ editor, file, position });
        });
        return;
      }

      files.forEach((file) => {
        const uploadId = uuid();
        const previewSrc = URL.createObjectURL(file);

        const insertAndUpload = async () => {
          // Get dimensions before inserting so skeleton matches
          const dimensions = await getImageDimensions(previewSrc);

          insertImageNode({
            editor,
            src: previewSrc,
            uploadId,
            position,
            width: dimensions?.width,
            height: dimensions?.height,
          });

          try {
            const upload = await createPostRequest({
              uploadId,
              file,
            });
            await uploadImageToS3({
              upload,
              file,
            });
          } catch (error) {
            removeImageNode({ editor, uploadId });
            console.error(error); // eslint-disable-line no-console
          }
        };

        insertAndUpload();
      });
    },
    [createPostRequest]
  );

  const handleCopyTemplateDocuments = useCallback<TipTapCopyTemplateDocumentsHandler>(
    async ({ templateDocuments }) => {
      if (!createPostRequest) return { uploadIdMapping: {}, srcMapping: {} };

      const uploadIdMapping: Record<string, string> = {};
      const srcMapping: Record<string, string> = {};

      await Promise.all(
        templateDocuments.map(async (doc) => {
          if (!doc.fromUploadId) return;

          try {
            // Download the image
            const downloadUrl = await getDownloadLink(doc.id, false);
            if (!downloadUrl) throw new Error("Failed to get download link");

            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error("Failed to download image");

            const blob = await response.blob();
            const file = new File([blob], doc.name, { type: doc.mimeType });

            // Create blob URL for immediate preview
            const previewUrl = URL.createObjectURL(blob);
            srcMapping[doc.fromUploadId] = previewUrl;

            // Upload to new text document
            const newUploadId = uuid();
            const upload = await createPostRequest({ uploadId: newUploadId, file });
            await uploadImageToS3({ upload, file });

            // Map old uploadId to new uploadId
            uploadIdMapping[doc.fromUploadId] = newUploadId;
          } catch (error) {
            console.error(`Failed to copy document ${doc.id}:`, error); // eslint-disable-line no-console
          }
        })
      );

      return { uploadIdMapping, srcMapping };
    },
    [createPostRequest, getDownloadLink]
  );

  const editorExtensions = useMemo(() => createTipTapExtensions(handleFileUpload), [handleFileUpload]);

  const editor = useEditor({
    extensions: editorExtensions,
    content: (parseTiptapContent(content) ?? "") as Content,
    editable,
    onCreate: ({ editor }) => {
      previousUploadIdsRef.current = getUploadIdsFromEditor(editor);
    },
    onUpdate: ({ editor }) => {
      if (suppressOnUpdate.current) return;

      // Detect removed documents
      if (onDocumentRemove && documents) {
        const currentUploadIds = getUploadIdsFromEditor(editor);
        previousUploadIdsRef.current.forEach((uploadId) => {
          if (!currentUploadIds.has(uploadId)) {
            const document = documents.find((doc) => doc.fromUploadId === uploadId);
            if (document) {
              onDocumentRemove({ documentId: document.id });
            }
          }
        });
        previousUploadIdsRef.current = currentUploadIds;
      }

      setHasPendingChanges(true);
      debouncedOnUpdate(JSON.stringify(editor.getJSON()));
    },
    onBlur: () => {
      debouncedOnUpdate.flush();
    },
  });

  useEffect(() => {
    if (!editable) editor?.commands.setContent((parseTiptapContent(content) ?? "") as Content, { emitUpdate: false });
  }, [content, editable, editor?.commands]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  useEffect(() => {
    if (!documents || documents.length === 0) return;

    const seenIds = new Set<string>();
    const uniqueDocuments = documents.filter((doc) => {
      if (seenIds.has(doc.id)) return false;
      seenIds.add(doc.id);
      return true;
    });

    const toFetchIds = new Set<string>();
    const pendingDocs = uniqueDocuments.filter((doc) => {
      const alreadyResolved = resolvedDocUrls[doc.id] !== undefined;
      const alreadyFailed = failedDocIds.has(doc.id);
      const inFlight = inFlightDocIds.current.has(doc.id);
      if (alreadyResolved || alreadyFailed || inFlight) return false;
      toFetchIds.add(doc.id);
      return true;
    });
    if (pendingDocs.length === 0) return;

    toFetchIds.forEach((id) => inFlightDocIds.current.add(id));

    const resolve = async () => {
      const successes: { doc: TipTapDocument; objectUrl: string }[] = [];
      const failures: string[] = [];

      await Promise.all(
        pendingDocs.map(async (doc) => {
          try {
            const url = await getDownloadLink(doc.id, false);
            if (!url) throw new Error("missing download url");
            const response = await fetch(url);
            if (!response.ok) throw new Error(`download failed ${response.status}`);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            successes.push({ doc, objectUrl });
          } catch (error) {
            failures.push(doc.id);
            console.error(error); // eslint-disable-line no-console
          }
        })
      );

      if (successes.length > 0) {
        setResolvedDocUrls((current) => {
          const next = { ...current };
          successes.forEach(({ doc, objectUrl }) => {
            const existing = current[doc.id];
            if (existing && existing !== objectUrl) {
              URL.revokeObjectURL(existing);
            }
            objectUrlCache.current[doc.id] = objectUrl;
            next[doc.id] = objectUrl;
          });
          return next;
        });
      }

      if (failures.length > 0) {
        setFailedDocIds((current) => new Set([...Array.from(current), ...failures]));
      }

      toFetchIds.forEach((id) => inFlightDocIds.current.delete(id));
    };

    void resolve();
  }, [documents, failedDocIds, getDownloadLink, resolvedDocUrls]);

  useEffect(() => {
    const cacheSnapshot = objectUrlCache.current;
    return () => {
      Object.values(cacheSnapshot).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (!editor || !documents || documents.length === 0) return;

    suppressOnUpdate.current = true;

    const existingSources = new Set<string>();
    const existingUploadIds = new Set<string>();
    editor.state.doc.descendants((node) => {
      if (node.type.name === "image" && typeof node.attrs.src === "string") {
        existingSources.add(node.attrs.src as string);
        if (typeof node.attrs.uploadId === "string") {
          existingUploadIds.add(node.attrs.uploadId);
        }
      }
    });

    documents.forEach((doc) => {
      const src = resolvedDocUrls[doc.id];
      if (!src) return;
      if (!doc.fromUploadId) return;

      if (existingUploadIds.has(doc.fromUploadId)) {
        updateImageNodeByUploadId({ editor, uploadId: doc.fromUploadId, src });
      }
    });

    suppressOnUpdate.current = false;
  }, [documents, editor, resolvedDocUrls]);

  const showSaveStatus = editable && onUpdate && debounceWait !== 0;

  return (
    <Flex
      flexDirection="column"
      border={hasBorder ? "1px solid" : ""}
      borderColor="smBorder"
      rounded="md"
      width="full"
      maxHeight={maxHeight}
    >
      {editor && (
        <>
          {editable ? (
            <Flex justifyContent="center" roundedTop="md" backgroundColor="smBackgroundSecondary" shadow="sm">
              <MenuBar
                editor={editor}
                onImageSelect={createPostRequest ? (files) => handleFileUpload({ editor, files }) : undefined}
                onCopyTemplateDocuments={createPostRequest ? handleCopyTemplateDocuments : undefined}
                toolbar={toolbar}
              />
            </Flex>
          ) : null}
          <Flex
            flexDirection="column"
            grow={1}
            minWidth="full"
            width="full"
            maxWidth="full"
            height="full"
            maxHeight="full"
            overflowX="auto"
            overflowY="auto"
            p={padding}
          >
            <Box>
              <EditorContent editor={editor} />
            </Box>
          </Flex>
          {showSaveStatus && (
            <Flex
              justifyContent="flex-end"
              alignItems="center"
              gap="2"
              px="2"
              py="1"
              borderTop="1px solid"
              borderColor="smBorder"
              backgroundColor="smBackgroundSecondary"
              roundedBottom="md"
            >
              <Text fontSize="xs" color="smMuted">
                {hasPendingChanges ? t("Unsaved changes") : (lastSavedLabel ?? t("No changes"))}
              </Text>
              {hasPendingChanges && (
                <Button size="xs" colorScheme="teal" variant="ghost" onClick={() => debouncedOnUpdate.flush()}>
                  {t("Save now")}
                </Button>
              )}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
